import React, { useState, useEffect } from 'react';

const COOKIE_CONSENT_KEY = 'jobasti_cookie_consent';

const CookieConsent = ({ onConsented }) => {
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    functional: true,
    analytics: true,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      onConsented?.();
    } else {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ essential: true, functional: true, analytics: true, timestamp: Date.now() }));
    setVisible(false);
    onConsented?.();
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ essential: true, functional: false, analytics: false, timestamp: Date.now() }));
    setVisible(false);
    onConsented?.();
  };

  const handleSaveConfig = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ ...preferences, timestamp: Date.now() }));
    setVisible(false);
    onConsented?.();
  };

  if (!visible) return null;

  const Toggle = ({ checked, disabled, onChange }) => (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      style={{
        width: 48, height: 26, borderRadius: 13,
        background: checked ? '#2563eb' : '#d1d5db',
        position: 'relative', border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.25s ease',
        opacity: disabled ? 0.7 : 1,
        flexShrink: 0,
      }}
    >
      <span style={{
        width: 20, height: 20, borderRadius: '50%',
        background: '#fff',
        position: 'absolute', top: 3,
        left: checked ? 25 : 3,
        transition: 'left 0.25s ease',
        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
      }} />
    </button>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      padding: '20px',
      backdropFilter: 'blur(4px)',
    }} data-testid="cookie-consent-overlay">
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        width: '100%',
        maxWidth: 520,
        padding: 0,
        boxShadow: '0 -8px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflow: 'hidden',
      }} data-testid="cookie-consent-modal">

        {/* Accent bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)' }} />

        <div style={{ padding: '28px 32px 28px' }}>
          {/* Logo */}
          <div style={{ marginBottom: 20 }}>
            <span style={{
              fontSize: 20, fontWeight: 800, color: '#2563eb', letterSpacing: '-0.5px',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
              Jobasti
            </span>
          </div>

          {!showConfig ? (
            <>
              <h2 style={{
                fontSize: 21, fontWeight: 700, color: '#0f172a',
                margin: '0 0 10px 0', lineHeight: 1.3,
              }}>
                We value your privacy
              </h2>
              <p style={{
                fontSize: 14, color: '#64748b', lineHeight: 1.7,
                margin: '0 0 24px 0',
              }}>
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. You can choose which cookies to accept.
              </p>

              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <button onClick={handleAccept} data-testid="cookie-accept-btn" style={{
                  flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 600,
                  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10,
                  cursor: 'pointer', transition: 'background 0.2s',
                }}>
                  Accept All
                </button>
                <button onClick={handleReject} data-testid="cookie-reject-btn" style={{
                  flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 600,
                  background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 10,
                  cursor: 'pointer', transition: 'background 0.2s',
                }}>
                  Reject All
                </button>
              </div>
              <button onClick={() => setShowConfig(true)} data-testid="cookie-configure-btn" style={{
                width: '100%', padding: '10px 0', fontSize: 13, fontWeight: 500,
                background: 'transparent', color: '#2563eb', border: 'none',
                cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3,
              }}>
                Manage preferences
              </button>
            </>
          ) : (
            <>
              <h2 style={{
                fontSize: 21, fontWeight: 700, color: '#0f172a',
                margin: '0 0 10px 0', lineHeight: 1.3,
              }}>
                Cookie preferences
              </h2>
              <p style={{
                fontSize: 13, color: '#64748b', lineHeight: 1.6,
                margin: '0 0 20px 0',
              }}>
                Essential cookies are always on. Toggle others below.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                {[
                  { key: 'essential', label: 'Essential', desc: 'Core site functionality', locked: true, checked: true },
                  { key: 'functional', label: 'Functional', desc: 'Preferences & personalization', locked: false, checked: preferences.functional },
                  { key: 'analytics', label: 'Analytics', desc: 'Usage data & improvements', locked: false, checked: preferences.analytics },
                ].map(item => (
                  <div key={item.key} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 14px', background: '#f8fafc', borderRadius: 10,
                    border: '1px solid #f1f5f9',
                  }}>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: 0 }}>{item.label}</p>
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: '2px 0 0 0' }}>{item.desc}</p>
                    </div>
                    <Toggle
                      checked={item.checked}
                      disabled={item.locked}
                      onChange={() => setPreferences(p => ({ ...p, [item.key]: !p[item.key] }))}
                    />
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowConfig(false)} style={{
                  flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 600,
                  background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 10,
                  cursor: 'pointer',
                }}>
                  Back
                </button>
                <button onClick={handleSaveConfig} data-testid="cookie-save-btn" style={{
                  flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 600,
                  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10,
                  cursor: 'pointer',
                }}>
                  Save preferences
                </button>
              </div>
            </>
          )}

          <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
            <a href="/privacy-policy" style={{ fontSize: 12, color: '#94a3b8', marginRight: 20, textDecoration: 'none' }}>Privacy Policy</a>
            <a href="/imprint" style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'none' }}>Imprint</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
